import { AbstractDestroyable } from '@deqo/foundation';
import { injectable } from 'inversify';
import { Rect } from '../../../platform/geom/Rect';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Colors } from '../../../ui';
import { resolve } from '../../Container';
import { AdapterInfo } from '../AdapterInfo';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { OverlayManager } from '../overlay/OverlayManager';
import { Net } from './Net';
import { NetRegister } from './NetRegister';

@injectable()
export class NetOverlay extends AbstractDestroyable {

    @resolve(Camera) private camera!: Camera;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(NetRegister) private nets!: NetRegister;
    @resolve(OverlayManager) private overlays!: OverlayManager;

    public init() {

        const handle = this.overlays.createOverlay('nets', this.doLayout, this.doPaint);
        this.overlays.add(handle);

        this.burden(handle);
        this.burden(this.camera.observe(() => handle.invalidate(true)));
        this.burden(this.nets.observe(() => handle.invalidate(true)));
        this.burden(this.session.observe(AdapterInfo, () => handle.invalidate()));

        return this;
    }

    private shouldPaint() {
        return this.session.get(AdapterInfo).target === 'none';
    }

    private doLayout = () => {
        return Array.from(this.nets.values()).flatMap(x => x.parts);
    };

    private doPaint = (gfx: UniversalSurfaceRenderer) => {
        if (!this.shouldPaint()) return;
        this.nets.forEach(([_, n]) => this.paintNet(gfx, n));
    };

    private paintNet(gfx: UniversalSurfaceRenderer, net: Net): void {
        if (!net.visible) return;
        const mvm = this.camera.modelViewMatrix;

        for (const { style, verticies } of net.parts) {
            gfx.reset();

            if (style === 'blob') {

                const mpt = (verticies.length > 1)
                    ? Rect.fromPoints(verticies).center()
                    : verticies[0];

                const vpt = mvm.apply(mpt);

                gfx.globalAlpha = .1;
                gfx.fillStyle = '#000';
                gfx.lineWidth = 1.5;
                gfx.fillCircle(vpt.x, vpt.y, 8);
            }
            else if (style === 'node') {

                const mpt = (verticies.length > 1)
                    ? Rect.fromPoints(verticies).center()
                    : verticies[0];

                const vpt = mvm.apply(mpt);
                const xywh = Rect.fromCenter(vpt, 3.5).toTuple();

                gfx.fillStyle = '#fafafa';
                gfx.strokeStyle = Colors.primaryLight;
                gfx.lineWidth = 1.5;
                gfx.fillCircle(vpt.x, vpt.y, 4.5);
                gfx.strokeCircle(vpt.x, vpt.y, 4.5);
                // gfx.fillRect(...xywh);
                // gfx.strokeRect(...xywh);
            }
            else if (style === 'polyline') {

                gfx.strokeStyle = Colors.primaryLight;
                gfx.lineWidth = 1.5;

                gfx.beginPath();
                mvm.apply(verticies).forEach((p, i) => {
                    if (i === 0) gfx.moveTo(p.x, p.y);
                    else gfx.lineTo(p.x, p.y);
                });
                gfx.stroke();
            }


        }

        // const border = Brush.create({ color: Colors.primaryText, width: 1.5 });
        // const background = Brush.create({ color: '#ffffff' });

        // for (const elmt of composition) {
        //     if (elmt instanceof Polyline) {
        //         surface.strokePolyline(border, elmt, true);
        //     }
        //     if (elmt instanceof Line) {
        //         surface.strokeLine(border, elmt);
        //     }
        //     if (elmt instanceof Point) {
        //         surface.fillCircle(background, elmt, 4.5);
        //         surface.strokeCircle(border, elmt, 4.5);
        //     }
        // }
    }

    // private paintDebugHandle(surface: Surface, net: Net, handle: NetHandle): void {
    //     const { camera } = this;

    //     const b = Brush.create({ color: 'red' });
    //     surface.fillPolyline(b, camera.toView(handle.verticies));
    // }
}
